import { ScopeReportLineItem } from '@kaiizen/kai-pro-api';
import { ContractorReportLineItems } from './ScopeReportTypes';

/**
 * Field selector type for sorting ScopeReportLineItem arrays
 */
export type ScopeReportLineItemSortField = keyof ScopeReportLineItem;

/**
 * Sort direction for sorting operations
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Sort configuration for sorting line items
 */
export interface SortConfig {
  field: ScopeReportLineItemSortField;
  direction?: SortDirection;
}

/**
 * Compare two values for sorting, handling null/undefined
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns Comparison result (-1, 0, or 1)
 */
const compareValues = <T>(a: T, b: T, direction: SortDirection = 'asc'): number => {
  const multiplier = direction === 'asc' ? 1 : -1;

  if (a === null || a === undefined) {
    return b === null || b === undefined ? 0 : multiplier;
  }
  if (b === null || b === undefined) {
    return -multiplier;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return multiplier * a.localeCompare(b);
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return multiplier * (a - b);
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return multiplier * (a === b ? 0 : a ? 1 : -1);
  }

  const aStr = String(a);
  const bStr = String(b);
  return multiplier * aStr.localeCompare(bStr);
};

/**
 * Create a comparator function for sorting ScopeReportLineItem arrays
 *
 * @param field - The field to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns Comparator function for use with Array.sort()
 */
const createLineItemComparator = (
  field: ScopeReportLineItemSortField,
  direction: SortDirection = 'asc',
): (a: ScopeReportLineItem, b: ScopeReportLineItem) => number => {
  return (a: ScopeReportLineItem, b: ScopeReportLineItem) => {
    const aValue = a[field];
    const bValue = b[field];
    return compareValues(aValue, bValue, direction);
  };
};

/**
 * Sort a single array of ScopeReportLineItem by a field
 *
 * @param lineItems - Array of line items to sort
 * @param field - Field to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns New sorted array (does not mutate input)
 */
export const sortLineItems = (
  lineItems: ScopeReportLineItem[],
  field: ScopeReportLineItemSortField,
  direction: SortDirection = 'asc',
): ScopeReportLineItem[] => {
  const comparator = createLineItemComparator(field, direction);
  return [...lineItems].sort(comparator);
};

export const DEFAULT_TEMPLATE_SORT_CONFIG: SortConfig[] = [];

/**
 * Sort a single array of ScopeReportLineItem using multiple sort criteria
 *
 * @param lineItems - Array of line items to sort
 * @param sortConfigs - Array of sort configurations (applied in order)
 * @returns New sorted array (does not mutate input)
 */
export const sortLineItemsByMultipleFields = (
  lineItems: ScopeReportLineItem[],
  sortConfigs: SortConfig[] = DEFAULT_TEMPLATE_SORT_CONFIG,
): ScopeReportLineItem[] => {
  if (sortConfigs.length === 0) {
    return [...lineItems];
  }

  const comparators = sortConfigs.map(config => createLineItemComparator(config.field, config.direction || 'asc'));

  return [...lineItems].sort((a, b) => {
    for (const comparator of comparators) {
      const result = comparator(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  });
};

/**
 * Sort all arrays in a ContractorReportLineItems object by a field
 *
 * This function sorts each contractor's line items array independently,
 * maintaining the contractor grouping structure.
 *
 * @param contractorLineItems - The contractor line items to sort
 * @param field - Field to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns New ContractorReportLineItems with sorted arrays (does not mutate input)
 */
export const sortContractorReportLineItems = (
  contractorLineItems: ContractorReportLineItems,
  field: ScopeReportLineItemSortField,
  direction: SortDirection = 'asc',
): ContractorReportLineItems => {
  const result: ContractorReportLineItems = {};
  Object.keys(contractorLineItems).forEach(contractorName => {
    result[contractorName] = sortLineItems(contractorLineItems[contractorName], field, direction);
  });
  return result;
};

/**
 * Sort all arrays in a ContractorReportLineItems object using multiple sort criteria
 *
 * This function sorts each contractor's line items array independently,
 * maintaining the contractor grouping structure.
 *
 * @param contractorLineItems - The contractor line items to sort
 * @param sortConfigs - Array of sort configurations (applied in order)
 * @returns New ContractorReportLineItems with sorted arrays (does not mutate input)
 */
export const sortContractorReportLineItemsByMultipleFields = (
  contractorLineItems: ContractorReportLineItems,
  sortConfigs: SortConfig[],
): ContractorReportLineItems => {
  const result: ContractorReportLineItems = {};
  Object.keys(contractorLineItems).forEach(contractorName => {
    result[contractorName] = sortLineItemsByMultipleFields(contractorLineItems[contractorName], sortConfigs);
  });
  return result;
};

export const GROUP_SORT_CONFIG: SortConfig[] = [
  { field: 'groupName', direction: 'asc' },
  { field: 'scopePDFTaskDescName', direction: 'asc' },
  { field: 'optionId', direction: 'asc' },
  { field: 'lineItemId', direction: 'asc' },
];

export const TRADE_SORT_CONFIG: SortConfig[] = [
  { field: 'trade', direction: 'asc' },
  { field: 'groupName', direction: 'asc' },
  { field: 'scopePDFTaskDescName', direction: 'asc' },
  { field: 'optionId', direction: 'asc' },
  { field: 'lineItemId', direction: 'asc' },
];
